import type { OrganizationMemberRoleName, UpdateOrganizationRequest } from "@multi-chat/shared";

export const ORGANIZATION_NAME_MAX_LENGTH = 200;

export type GeneralSettingsSnapshot = {
  organizationId: number;
  name: string;
  timezone: string;
};

export function canEditOrganizationSettings(role: OrganizationMemberRoleName): boolean {
  return role === "OWNER" || role === "ADMIN";
}

export function trimmedOrganizationName(name: string): string {
  return name.trim();
}

export function isOrganizationNameValid(name: string): boolean {
  const trimmed = trimmedOrganizationName(name);
  return trimmed.length >= 1 && trimmed.length <= ORGANIZATION_NAME_MAX_LENGTH;
}

export function isGeneralFormDirty(
  loaded: GeneralSettingsSnapshot | null,
  name: string,
  timezone: string,
): boolean {
  if (!loaded) return false;
  return loaded.name !== trimmedOrganizationName(name) || loaded.timezone !== timezone;
}

export function isTimezoneAllowed(
  timezone: string,
  loadedTimezone: string,
  knownTimezones: ReadonlySet<string>,
): boolean {
  if (timezone.length === 0) return false;
  if (timezone === loadedTimezone) return true;
  return knownTimezones.has(timezone);
}

export function buildOrganizationPatch(
  loaded: GeneralSettingsSnapshot,
  name: string,
  timezone: string,
): UpdateOrganizationRequest | null {
  const trimmed = trimmedOrganizationName(name);
  const patch: UpdateOrganizationRequest = {};
  if (trimmed !== loaded.name) {
    patch.name = trimmed;
  }
  if (timezone !== loaded.timezone) {
    patch.timezone = timezone;
  }
  if (patch.name === undefined && patch.timezone === undefined) {
    return null;
  }
  return patch;
}

export function canSubmitGeneralSettings(input: {
  role: OrganizationMemberRoleName;
  loadedOrganizationId: number | null;
  selectedOrganizationId: number;
  dirty: boolean;
  saving: boolean;
  nameValid: boolean;
  timezoneValid: boolean;
}): boolean {
  return (
    canEditOrganizationSettings(input.role) &&
    input.loadedOrganizationId === input.selectedOrganizationId &&
    input.dirty &&
    !input.saving &&
    input.nameValid &&
    input.timezoneValid
  );
}

export function snapshotFromOrganization(details: {
  id: number;
  name: string;
  timezone: string;
}): GeneralSettingsSnapshot {
  return {
    organizationId: details.id,
    name: details.name,
    timezone: details.timezone,
  };
}
